import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { 
  generatePayslipPDF, 
  getAvailablePayslipMonths,
  validatePayslipParams 
} from '../../../services/payslipService';

/**
 * PayslipGenerator Component
 * Allows VAs to generate PDF payslips for previous months
 * Shows month selector and generates branded PDF documents
 */
const PayslipGenerator = ({ userProfile, contracts }) => {
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedContract, setSelectedContract] = useState('all');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingMonths, setIsLoadingMonths] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch available months on mount
  useEffect(() => {
    const fetchMonths = async () => {
      if (!userProfile?.id || !contracts || contracts.length === 0) {
        setIsLoadingMonths(false);
        return;
      }

      try {
        setIsLoadingMonths(true);
        const months = await getAvailablePayslipMonths(userProfile.id, contracts);
        setAvailableMonths(months);
        
        // Auto-select most recent month if available
        if (months.length > 0) {
          setSelectedMonth(months[0].value);
        }
      } catch (err) {
        console.error('[PayslipGenerator] Error fetching months:', err);
        setError('Failed to load available months');
      } finally {
        setIsLoadingMonths(false);
      }
    };

    fetchMonths();
  }, [userProfile?.id, contracts]);

  const handleGeneratePayslip = async () => {
    try {
      setIsGenerating(true);
      setError(null);
      setSuccess(null);

      // Parse selected month
      if (!selectedMonth) {
        throw new Error('Please select a month');
      }

      const [year, month] = selectedMonth.split('-').map(Number);

      // Validate parameters
      const validation = validatePayslipParams(userProfile, contracts, month, year);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Generate payslip
      const result = await generatePayslipPDF({
        vaId: userProfile.id,
        userProfile,
        contracts,
        month,
        year,
        contractId: selectedContract === 'all' ? null : selectedContract
      });

      setSuccess(`Payslip generated successfully: ${result.filename}`);
      console.log('[PayslipGenerator] Payslip generated:', result);
    } catch (err) {
      console.error('[PayslipGenerator] Generation failed:', err);
      setError(err.message || 'Failed to generate payslip');
    } finally {
      setIsGenerating(false);
    }
  };

  // Don't render if no contracts
  if (!contracts || contracts.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-primary-50 to-indigo-50 rounded-lg border border-primary-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Icon name="FileText" size={20} className="text-primary-600" />
            Generate Payslip
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Download PDF payslips for previous months with approved entries
          </p>
        </div>
      </div>

      {/* Loading State */}
      {isLoadingMonths && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-gray-600">Loading available months...</p>
        </div>
      )}

      {/* No Months Available */}
      {!isLoadingMonths && availableMonths.length === 0 && (
        <div className="text-center py-6">
          <Icon name="Calendar" size={40} className="text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600">
            No approved timesheet entries found. Complete and get your timesheets approved to generate payslips.
          </p>
        </div>
      )}

      {/* Generator Form */}
      {!isLoadingMonths && availableMonths.length > 0 && (
        <div className="space-y-4">
          {/* Month Selector */}
          <div>
            <label htmlFor="payslip-month" className="block text-sm font-medium text-gray-700 mb-2">
              Select Month
            </label>
            <select
              id="payslip-month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              disabled={isGenerating}
            >
              <option value="">-- Select a month --</option>
              {availableMonths.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          {/* Contract Selector (if multiple contracts) */}
          {contracts.length > 1 && (
            <div>
              <label htmlFor="payslip-contract" className="block text-sm font-medium text-gray-700 mb-2">
                Select Contract (Optional)
              </label>
              <select
                id="payslip-contract"
                value={selectedContract}
                onChange={(e) => setSelectedContract(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                disabled={isGenerating}
              >
                <option value="all">All Contracts</option>
                {contracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    {contract.employer?.company_name || contract.employer?.full_name || 'Unknown Employer'}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Generate combined payslip for all contracts or select a specific one
              </p>
            </div>
          )}

          {/* Generate Button */}
          <Button
            variant="primary"
            iconName="Download"
            onClick={handleGeneratePayslip}
            disabled={isGenerating || !selectedMonth}
            className="w-full"
          >
            {isGenerating ? (
              <>
                <span className="animate-spin inline-block mr-2">⏳</span>
                Generating Payslip...
              </>
            ) : (
              'Generate Payslip PDF'
            )}
          </Button>

          {/* Success Message */}
          {success && (
            <div className="flex items-start gap-2 p-3 bg-success-50 border border-success-200 rounded-lg">
              <Icon name="CheckCircle" size={18} className="text-success-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-success-700">{success}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-error-50 border border-error-200 rounded-lg">
              <Icon name="AlertCircle" size={18} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error-700">{error}</p>
            </div>
          )}

          {/* Info Note */}
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Icon name="Info" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700">
              <p className="font-medium mb-1">Payslip includes:</p>
              <ul className="list-disc list-inside space-y-0.5 ml-1">
                <li>All approved timesheet entries for the selected month</li>
                <li>Detailed breakdown by date and project</li>
                <li>Hourly rates and total earnings</li>
                <li>VA Sprouts official branding</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

PayslipGenerator.propTypes = {
  userProfile: PropTypes.shape({
    id: PropTypes.string.isRequired,
    full_name: PropTypes.string,
    email: PropTypes.string,
    location: PropTypes.string
  }).isRequired,
  contracts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      hourly_rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      employer: PropTypes.shape({
        id: PropTypes.string,
        full_name: PropTypes.string,
        company_name: PropTypes.string,
        email: PropTypes.string
      })
    })
  ).isRequired
};

export default PayslipGenerator;
